/**
 * Appeal history tab for the Appeal Info modal left pane.
 */

import { useEffect, useMemo, useState } from "react";
import { fetchAppealDetail } from "../../../../api/postCall/fetchAppealDetail";
import { AppealHistoryEvent } from "../../../../data-model/appealDetail";
import { CaseAppealsTableRowData } from "../../../../data-model/caseAppealsTable";
import { logError } from "../../../../utils/customLogger";
import AppealHistoryTimelineItem from "../../../sidePanel/appealInfoSidePanel/components/appealHistoryTimelineItem";
import {
  buildAppealHistoryTimelineEntries,
  normalizeAppealHistoryEvents,
} from "../../../sidePanel/appealInfoSidePanel/utils/formatAppealHistoryEvent";

interface HistoryPaneProps {
  data: CaseAppealsTableRowData;
}

/**
 * Loads and renders appeal audit events in a vertical timeline.
 *
 * @param props.data - Appeal table row used to load history by identifier
 * @returns Appeal history pane content
 */
const HistoryPane = (props: HistoryPaneProps) => {
  const [events, setEvents] = useState<AppealHistoryEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    const identifier = props.data?.identifier?.trim();
    if (!identifier) {
      setLoading(false);
      setErrorMessage("Order identifier is missing.");
      return;
    }

    let cancelled = false;

    const loadHistory = async () => {
      setLoading(true);
      setErrorMessage(null);

      try {
        const details = await fetchAppealDetail({ identifier });
        if (cancelled) {
          return;
        }

        setEvents(normalizeAppealHistoryEvents(details.events));
      } catch (error) {
        if (cancelled) {
          return;
        }

        logError(error as Error, "AppealInfoHistoryPane.loadHistory");
        const message =
          error instanceof Error
            ? error.message
            : "Failed to load appeal history.";
        setErrorMessage(message);
        setEvents([]);
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    void loadHistory();

    return () => {
      cancelled = true;
    };
  }, [props.data?.identifier]);

  const timelineEntries = useMemo(
    () => buildAppealHistoryTimelineEntries(events),
    [events],
  );

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="flex-1 overflow-y-auto px-5 py-6">
        {loading && (
          <div className="flex flex-col items-center justify-center gap-2 py-16">
            <div
              className="h-8 w-8 animate-spin rounded-full border-2 border-primaryGray-12 border-t-primaryGray-4"
              aria-hidden
            />
            <p className="m-0 text-small text-primaryGray-6">
              Loading appeal history…
            </p>
          </div>
        )}

        {!loading && errorMessage && (
          <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-primaryGray-15 bg-white/80 py-14 text-center">
            <p className="m-0 text-small font-medium text-primaryGray-1">
              Could not load appeal history
            </p>
            <p className="mt-1 max-w-sm text-tiny text-primaryGray-6">
              {errorMessage}
            </p>
          </div>
        )}

        {!loading && !errorMessage && timelineEntries.length === 0 && (
          <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-primaryGray-15 bg-white/80 py-14 text-center">
            <p className="m-0 text-small font-medium text-primaryGray-1">
              No appeal history yet
            </p>
            <p className="mt-1 max-w-sm text-tiny text-primaryGray-6">
              Updates and actions for this appeal will appear here.
            </p>
          </div>
        )}

        {!loading && !errorMessage && timelineEntries.length > 0 && (
          <div className="flex flex-col">
            {timelineEntries.map((entry, index) => (
              <AppealHistoryTimelineItem
                key={`${entry.timeLabel}-${entry.dateLabel}-${entry.actorName}-${index}`}
                entry={entry}
                isLast={index === timelineEntries.length - 1}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default HistoryPane;
